import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  HandymanOutlined as HandymanIcon,
  ForumOutlined as ForumIcon,
  EventOutlined as EventIcon,
  StorefrontOutlined as StorefrontIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { CONTENT_MAX_WIDTH } from '../components/layout/PageLayout';
import { useAuth } from '../context/AuthContext';
import { useFeed } from '../hooks/community/useFeed';
import { useAgenda } from '../hooks/community/useAgenda';
import { Loading, RowsSkeleton } from '../components/layout/Skeletons';
import { eventDayParts, formatEventDate, formatRelativeDate } from '../constants/community';
import ModeratorBadge from '../components/community/ModeratorBadge';

/**
 * Three roofs and a sun, drawn rather than photographed.
 *
 * A directory of a single neighbourhood has no stock photography that is
 * actually of that neighbourhood, and a generic one reads as a placeholder.
 * A line drawing promises nothing it cannot keep.
 */
function Rooftops() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'flex-end',
        justifyContent: 'center',
        flexShrink: 0,
        width: 420,
        height: 300,
        borderRadius: '24px',
        bgcolor: 'success.light',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <svg width="420" height="230" viewBox="0 0 420 230" fill="none">
        <path d="M0 205h420" stroke="#b9c4a6" strokeWidth="2" />
        <path d="M40 205v-78l52-42 52 42v78z" fill="#fffdf8" stroke="#9aa98a" strokeWidth="2" strokeLinejoin="round" />
        <path d="M24 129 92 74l68 55" stroke="#a84a26" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="74" y="152" width="36" height="53" fill="#eef1e6" stroke="#9aa98a" strokeWidth="2" />
        <path d="M164 205v-104l58-46 58 46v104z" fill="#fffdf8" stroke="#9aa98a" strokeWidth="2" strokeLinejoin="round" />
        <path d="M148 103 222 45l74 58" stroke="#a84a26" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="196" y="140" width="52" height="65" fill="#eef1e6" stroke="#9aa98a" strokeWidth="2" />
        <path d="M222 140v65M196 170h52" stroke="#9aa98a" strokeWidth="2" />
        <path d="M300 205v-70l46-37 46 37v70z" fill="#fffdf8" stroke="#9aa98a" strokeWidth="2" strokeLinejoin="round" />
        <path d="M286 137 346 89l60 48" stroke="#a84a26" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="330" y="158" width="34" height="47" fill="#eef1e6" stroke="#9aa98a" strokeWidth="2" />
        <circle cx="346" cy="60" r="13" fill="#f7ecdc" stroke="#c07a1e" strokeWidth="2" />
      </svg>
    </Box>
  );
}

/**
 * One of the three ways in.
 *
 * Each one is named, described, and ends in a verb. The previous home page
 * offered four bare buttons, which asks the reader to already know what
 * "L'agenda" contains.
 *
 * Keep `action` under about eighteen characters. Four of these share the row on
 * a wide screen, which leaves roughly 219px inside the button — "Chercher un
 * professionnel" needed 257 and wrapped onto two lines, on that card alone.
 */
function Doorway({ icon, tint, iconColor, title, description, action, to }) {
  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: 3.25, height: '100%' }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '16px',
            bgcolor: tint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 30, color: iconColor } })}
        </Box>
        <Typography variant="h4" component="h2">
          {title}
        </Typography>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {description}
        </Typography>
        <Button component={RouterLink} to={to} variant="contained" fullWidth>
          {action}
        </Button>
      </CardContent>
    </Card>
  );
}

/** A panel of live rows, with its own "see everything" escape. */
function Digest({ title, to, empty, loading, children }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.25, pb: 2.5 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 0.5 }}
        >
          <Typography variant="h3" component="h2">
            {title}
          </Typography>
          <Button component={RouterLink} to={to} size="small" variant="text" color="primary">
            Tout voir
          </Button>
        </Stack>
        {loading ? (
          <Loading label={`Chargement : ${title}`}>
            <RowsSkeleton count={3} />
          </Loading>
        ) : (
          children || (
            <Typography variant="body2" sx={{ py: 2 }}>
              {empty}
            </Typography>
          )
        )}
      </CardContent>
    </Card>
  );
}

function PostRow({ post }) {
  const tint = (theme) => theme.palette.postTypes[post.type] || { bg: '#f0ece0', ink: '#564f44' };

  return (
    <Box
      component={RouterLink}
      to={`/quartier/${post.uuid}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.9,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'surface.rule',
        textDecoration: 'none',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover h3': { textDecoration: 'underline' },
      }}
    >
      <Chip
        label={post.type_label}
        size="small"
        sx={{
          alignSelf: 'flex-start',
          bgcolor: (theme) => tint(theme).bg,
          color: (theme) => tint(theme).ink,
          fontWeight: 700,
        }}
      />
      <Typography variant="h6" component="h3" sx={{ fontFamily: 'inherit', fontSize: '1.0625rem', fontWeight: 600 }}>
        {post.title}
      </Typography>
      <Typography variant="caption">
        {post.author}
        {post.author_is_moderator && <ModeratorBadge show />} · {post.neighborhood ? `${post.neighborhood.name} · ` : ''}
        {formatRelativeDate(post.created_at)}
      </Typography>
    </Box>
  );
}

function EventRow({ event }) {
  const parts = eventDayParts(event.starts_at);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'surface.rule',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 58,
          flexShrink: 0,
          textAlign: 'center',
          py: 1,
          borderRadius: '12px',
          bgcolor: 'success.light',
        }}
      >
        <Typography variant="h5" component="span" sx={{ display: 'block', lineHeight: 1, color: 'secondary.main' }}>
          {parts ? parts.day : '—'}
        </Typography>
        <Typography
          variant="overline"
          component="span"
          sx={{ display: 'block', fontSize: '0.78125rem', color: 'secondary.light', mt: 0.4 }}
        >
          {parts ? parts.month : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        <Typography variant="h6" component="h3" sx={{ fontFamily: 'inherit', fontSize: '1.0625rem', fontWeight: 600 }}>
          {event.title}
        </Typography>
        <Typography variant="caption">{formatEventDate(event.starts_at)}</Typography>
      </Box>
    </Box>
  );
}

/**
 * The front door.
 *
 * Searching the directory is the commonest reason to come here at all, so the
 * search field is on the page itself rather than one click away. The two
 * panels below are live: a hub whose home page cannot show a single real post
 * looks abandoned, and that is a thing residents decide in about two seconds.
 */
function Home() {
  const { isAuthenticated, isProvider, loading: authLoading, getShortName } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const { posts, loading: loadingPosts } = useFeed();
  const { events, loading: loadingEvents } = useAgenda();

  // Everything, not the directory. Somebody at the front door does not know
  // whether what they are after is a listing, a shop, a post or an event —
  // the sections are the hub's vocabulary, not theirs. This used to land on
  // the services page, which since the shops moved out could not answer its
  // own example: "boulangerie".
  const search = (event) => {
    event.preventDefault();
    navigate(query.trim() ? `/recherche?q=${encodeURIComponent(query.trim())}` : '/recherche');
  };

  return (
    <Box sx={{ bgcolor: 'background.default', flex: 1 }}>
      <Container maxWidth={CONTENT_MAX_WIDTH} sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 6 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 6, alignItems: 'center', mb: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1, width: '100%' }}>
            {/* The line keeps its place whether or not there is a name to put
                in it: filling it in once the session answers used to push the
                headline, and everything under it, down by 46px. */}
            <Typography
              variant="subtitle1"
              component="p"
              sx={{ fontWeight: 600, color: 'primary.main', minHeight: 26 }}
            >
              {isAuthenticated ? `Bonjour, ${getShortName()}` : ''}
            </Typography>

            <Typography variant="h1" sx={{ fontSize: { xs: '2.25rem', md: '2.625rem' } }}>
              Tout le quartier, au même endroit.
            </Typography>

            <Typography variant="subtitle1" component="p" sx={{ maxWidth: 520 }}>
              Les artisans et commerçants près de chez vous, ce qui se dit entre voisins, et ce qui se
              passe cette semaine.
            </Typography>

            <Box component="form" onSubmit={search} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 560 }}>
              <Typography component="label" htmlFor="home-search" variant="subtitle2">
                Que cherchez-vous&nbsp;?
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  id="home-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Un métier, un commerce, un mot-clé"
                  fullWidth
                  InputProps={{
                    sx: { borderRadius: 999 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" size="large" sx={{ flexShrink: 0 }}>
                  Chercher
                </Button>
              </Stack>
              <Typography variant="caption">
                Cherche dans les services, les commerces, le fil et l’agenda. Par
                exemple&nbsp;: plombier, boulangerie, vide-grenier.
              </Typography>
            </Box>
          </Box>

          <Rooftops />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 2.75,
          }}
        >
          <Doorway
            icon={<HandymanIcon />}
            tint="#f7ecdc"
            iconColor="primary.main"
            title="Les services"
            description="Plombiers, électriciens, garde d'enfants… ceux qui se déplacent chez vous, notés par vos voisins."
            action="Voir les services"
            to="/services"
          />
          <Doorway
            icon={<StorefrontIcon />}
            tint="#f0e7e0"
            iconColor="#8a5a3b"
            title="Les commerces"
            description="Les boutiques du quartier, leurs horaires, et ce qu'elles proposent cette semaine."
            action="Voir les commerces"
            to="/commerces"
          />
          <Doorway
            icon={<ForumIcon />}
            tint="#eef1e6"
            iconColor="secondary.main"
            title="Le fil du quartier"
            description="Une question, un bon plan, un chat perdu : ce que les habitants se disent au jour le jour."
            action="Lire le fil"
            to="/quartier"
          />
          <Doorway
            icon={<EventIcon />}
            tint="#eae6ef"
            iconColor="#5d4a6b"
            title="L'agenda"
            description="Vide-greniers, réunions de quartier, concerts : ce qui se passe dans les prochains jours."
            action="Voir l'agenda"
            to="/agenda"
          />
        </Box>

        {/* Equal halves. The feed used to get 1.15 of the width against the
            agenda's 1, which made two panels that carry the same three rows
            look like one matters more. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3.5,
            mt: 5,
            alignItems: 'start',
          }}
        >
          <Digest
            title="Ce qui se dit en ce moment"
            to="/quartier"
            loading={loadingPosts}
            empty="Le fil est encore vide. Soyez le premier à écrire quelque chose."
          >
            {posts.length > 0 ? posts.slice(0, 3).map((post) => <PostRow key={post.uuid} post={post} />) : null}
          </Digest>

          <Digest
            title="Prochainement"
            to="/agenda"
            loading={loadingEvents}
            empty="Rien de prévu pour l'instant dans le quartier."
          >
            {events.length > 0 ? events.slice(0, 3).map((event) => <EventRow key={event.uuid} event={event} />) : null}
          </Digest>
        </Box>

        {/* A professional is a resident too: this is an invitation on the way
            past, never a gate. Hidden once they have taken it.

            Waits for the session: `isProvider` reads false while it loads, so
            without this the band appeared for about 700ms on every load and
            then vanished — inviting professionals to do the thing they had
            already done, and moving the foot of the page as it went. */}
        {!authLoading && !isProvider && (
          <Box
            sx={{
              mt: 5,
              borderRadius: '18px',
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5 }}>
              <StorefrontIcon sx={{ fontSize: 40, color: '#c9d6b6', display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Typography variant="h3" component="h2" sx={{ color: 'inherit' }}>
                  Vous avez un commerce ou une activité&nbsp;?
                </Typography>
                <Typography variant="body2" sx={{ color: '#dfe6d6' }}>
                  Créez votre fiche gratuitement. Elle est relue avant d'être publiée.
                </Typography>
              </Box>
            </Stack>
            <Button
              component={RouterLink}
              to={isAuthenticated ? '/pro/bienvenue' : '/register'}
              variant="contained"
              size="large"
              endIcon={<ChevronRightIcon />}
              sx={{
                flexShrink: 0,
                bgcolor: 'background.paper',
                color: 'secondary.main',
                '&:hover': { bgcolor: '#f0ece0' },
              }}
            >
              Inscrire mon activité
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default Home;
